import { useEffect, useState } from 'react'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { emit } from '../../platform/analytics/analytics'
import './errors.css'

/**
 * "Something broke on our side" — the whole-page crash screen.
 *
 * This is what a ROOT-level `ErrorBoundary` passes as its `fallback`, wired in
 * `main.tsx`. It is distinct from `ErrorBoundary`'s own default fallback (an
 * `Async` error panel, sized for one broken widget or route) the same way a
 * hull breach is distinct from one dead gauge: the boundary this backs sits
 * above everything, so there is no sidebar, no top bar and — per the team
 * lead's brief — no guarantee of a `<Router>` above it either. Every control
 * below is therefore a plain element, never `react-router`'s `<Link>` or
 * `useNavigate`, which both throw outside a `<Router>`. A real `<a href>`
 * degrades to nothing worse than a full page load, which is also the one
 * recovery action that is guaranteed to work: this boundary exists because
 * something threw during a RENDER, and a client-side re-render of the same
 * tree throws again on the same props (`ErrorBoundary`'s own reasoning for
 * "Reload", not "Retry"). A full navigation is a fresh module evaluation,
 * not a re-render.
 *
 * ## The line this rewrites
 *
 * The comp's paragraph says *"We logged the failure and the on-call engineer
 * has already been paged."* Neither half is true: `ErrorBoundary` writes to
 * `console.error` and nothing leaves the browser (#112 is the ticket that
 * would change that, and it has not landed), and there is no on-call rotation
 * wired to this app at all. Telling a user help is already on the way when
 * nobody has been told anything is worse than saying nothing — it spends
 * trust the next real incident will need. What replaces it says only what is
 * true: the failure is in this browser's own console, and the user's data is
 * untouched (which IS true — a render-time throw happens after every write
 * that reaches this session has already round-tripped the API).
 *
 * ## The reference code — kept, deliberately, and logged so it means something
 *
 * There is no error-reporting backend (#112 again), so a reference minted
 * here corresponds to nothing on OUR side — quoting it to a support agent
 * today buys nothing a plain "it crashed" report would not. The reason to
 * show one anyway is the other side of that sentence: it *does* correspond to
 * something on the USER's side. `console.error` output survives in the
 * user's own devtools for as long as the tab stays open, and a support
 * conversation can ask "what does your console say near <reference>?" — a
 * question with an answer only because this component logs the same string
 * it prints on screen, in the same tick it mounts. Omitting the reference
 * entirely would have been the more defensible choice if this screen could
 * not back it with anything; logging it is what earns it a place in the UI.
 * It is not wired into `ErrorBoundary.componentDidCatch`'s own log line
 * (`ErrorBoundary.tsx` is out of scope for this change) so it will not sit on
 * the exact line carrying the stack trace — but it lands in the same stream,
 * moments later, which is what "in your console" promises and no more.
 */

export type AppCrashedProps = {
  /** The failing section, from the boundary that caught it — `ErrorBoundary`'s
   *  own `name` prop, unchanged. */
  name: string
  /**
   * When the crash was caught. Defaults to the moment this screen mounts —
   * the closest available proxy, since a static `fallback` node has no way to
   * receive the instant `componentDidCatch` actually ran. Injectable so a
   * test's printed timestamp is deterministic rather than racing `Date.now()`.
   */
  at?: Date
}

/**
 * Crockford's Base32 — excludes `I`, `L`, `O`, `U` so a transcribed reference
 * can't be misread as `1`/`0`, matching the comp's own `err_7QK2M4ZB` shape.
 */
const REFERENCE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * `crypto.getRandomValues`, not `crypto.randomUUID`.
 *
 * `randomUUID` is secure-context-only and throws on the plain-http LAN origin
 * this repo uses for real-device testing (`deviceId.ts`, `ToastProvider.tsx`
 * carry the same note) — the one function that must never be why a crash
 * screen itself crashes. `getRandomValues` has no such restriction.
 */
function mintReference(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  const body = Array.from(bytes, (byte) => REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length]).join('')
  return `err_${body}`
}

/**
 * Locale fixed to en-GB, matching `Async.tsx`'s own `DATE` formatter — every
 * date in this app wants the user's locale and there is no locale source yet
 * (that file's comment tracks it as a known gap, not a choice made here).
 * `timeZoneName: 'short'` asks the platform for whatever abbreviation it
 * actually has rather than inventing one — the brief is explicit that no
 * timezone label may be hard-coded.
 */
const WHEN_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
})

export function AppCrashed({ name, at }: AppCrashedProps) {
  // `useState(() => …)` so the fallback timestamp and the minted reference are
  // each computed once, at the first render, rather than on every one — a
  // caller re-rendering this static screen must not mint a second reference
  // or drift the timestamp forward.
  const [mountedAt] = useState(() => new Date())
  const [reference] = useState(() => mintReference())
  const when = at ?? mountedAt

  // Logged once, at mount, in the SAME console the boundary's own
  // `componentDidCatch` already wrote to — see the docblock above for why
  // this is what makes showing a reference honest rather than decorative.
  // An effect, not inline in the render body: `console.error` is a side
  // effect and render must stay pure, the same reason `Async`'s own
  // announcement is written from `useEffect` rather than during render.
  //
  // `app_crashed` lives in the same effect — #274. No properties: `name` is
  // the failing SECTION, free text in general even though this particular
  // caller (main.tsx's root boundary) always passes the fixed string
  // "TrackE5", and CLAUDE.md rule 7 refuses free text regardless of what one
  // call site happens to send today. The only thing worth knowing from this
  // screen is that a whole-app crash happened at all — the same "no
  // properties" call `password_reset_completed` makes in events.ts, for the
  // same reason.
  useEffect(() => {
    console.error(`AppCrashed: reference ${reference} for "${name}", caught ${when.toISOString()}`)
    emit('app_crashed')
  }, [reference, name, when])

  return (
    <main className="crashed enter">
      <div className="crashed__card">
        <div className="crashed__header">
          <span className="crashed__icon" aria-hidden="true">
            !
          </span>
          <div className="crashed__titles">
            <h1 className="crashed__title">Something broke on our side</h1>
            <span className="crashed__subtitle">Not your data — this was our error</span>
          </div>
        </div>

        <p className="crashed__message" role="alert">
          Your accounts, transactions and budgets are untouched — this was a display error on our
          side, not a change to anything you own. It&apos;s recorded in this browser&apos;s console
          under the reference below, in case a conversation with support needs it.
        </p>

        <dl className="crashed__meta">
          <div className="crashed__meta-row">
            <dt className="crashed__meta-label">What failed</dt>
            <dd className="crashed__meta-value">{name}</dd>
          </div>
          <div className="crashed__meta-row">
            <dt className="crashed__meta-label">When</dt>
            <dd className="crashed__meta-value crashed__meta-value--tabular">
              <time dateTime={when.toISOString()}>{WHEN_FORMAT.format(when)}</time>
            </dd>
          </div>
          <div className="crashed__meta-row">
            <dt className="crashed__meta-label">Reference</dt>
            <dd className="crashed__meta-value crashed__meta-value--mono">{reference}</dd>
          </div>
        </dl>

        <div className="crashed__actions">
          {/* A real navigation, not `useNavigate` — see the docblock. `/overview`
              rather than `/` so a plain full-page load lands directly, without
              depending on `App.tsx`'s client-side `<Navigate>` redirect having a
              router to run in. */}
          <ButtonLink href="/overview" variant="primary">
            Try again
          </ButtonLink>
          <ButtonLink href={`/help?ref=${reference}`} variant="chip">
            Send the reference to support
          </ButtonLink>
        </div>
      </div>
    </main>
  )
}
